import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "@@/shared/date-time/iso-and-unix";
import { timeZodSchema, type Time } from "@@/shared/date-time/time";
import { idZodSchema } from "@@/shared/id";
import { ownerIdZodSchema, type OwnerId } from "@@/shared/owner-id";
import { percentageZodSchema, type Percentage } from "@@/shared/percentage";
import type { Price} from "@@/shared/prices/price";
import { priceZodSchema } from "@@/shared/prices/price";
import type {
    Translatable} from "@@/shared/translations/translation";
import {
    translatableZodSchema,
} from "@@/shared/translations/translation";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { FeeSource} from "../fee-sources/fee-source";
import { feeSourceZodSchema } from "../fee-sources/fee-source";
import type { FeeTarget} from "../fee-target";
import { feeTargetZodSchema } from "../fee-target";

type FeeCreateCommand = {
    readonly _id: string;
    readonly _type: "FEE_CREATE_COMMAND";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly ownerIds: OwnerId[];
    readonly title: Translatable;
    readonly description: Translatable;
    readonly validTrough: Time;
    readonly feeTarget: FeeTarget;
    readonly type: CreateGeneralFee | CreateValueFee;
};

type CreateValueFee = {
    readonly type: "VALUE_FEE";
    readonly price: Price;
};

type CreateGeneralFee = {
    readonly type: "GENERAL_FEE";
    readonly feeSource: FeeSource;
    readonly percentage?: {
        percentage: Percentage;

        /**
         *Should Fee be use incl or escl vatAmount
         *
         * @type {boolean}
         */
        readonly includeVatAmount: boolean;
    };

    /**
     * If fee is lower then minValue, use minValue
     *
     * @type {Price}
     */
    readonly minValue?: Price;
    /**
     * Added flatFee on fee
     *
     * @type {Price}
     */
    readonly flatFee: Price;
};

type FactoryParams = Except<FeeCreateCommand, "_id" | "_type" | "_createdAt">;
const feeCreateCommandFactory = (params: FactoryParams) => {
    const command: FeeCreateCommand = {
        ...params,
        _id: generateId({ prefix: "fee_create_command__" }),
        _type: "FEE_CREATE_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return command;
};

const valueFeeZodSchema: z.ZodSchema<CreateValueFee> = z.object({
    type: z.literal("VALUE_FEE"),
    price: priceZodSchema,
});

const generelFeeZodSchema = z.object({
    type: z.literal("GENERAL_FEE"),
    percentage: z
        .object({
            percentage: percentageZodSchema,
            includeVatAmount: z.boolean(),
        })
        .optional(),

    minValue: priceZodSchema.optional(),
    flatFee: priceZodSchema,
    feeSource: feeSourceZodSchema,
});

const feeCreateZodSchema = z.union([valueFeeZodSchema, generelFeeZodSchema]);

const feeCreateCommandZodScheme: z.ZodSchema<FeeCreateCommand> = z.object({
    _id: idZodSchema,
    _type: z.literal("FEE_CREATE_COMMAND"),
    _createdAt: isoAndUnixZodSchema,
    ownerIds: z.array(ownerIdZodSchema),
    title: translatableZodSchema,
    description: translatableZodSchema,
    feeTarget: feeTargetZodSchema,
    validTrough: timeZodSchema,
    type: feeCreateZodSchema,
});

export {
    feeCreateCommandFactory,
    feeCreateCommandZodScheme,
    feeCreateZodSchema,
    generelFeeZodSchema,
};
export type {
    CreateGeneralFee,
    CreateValueFee,
    FeeCreateCommand,
    FactoryParams as FeeCreateCommandFactoryParams,
};
